import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import { onCLS, onINP, onLCP } from 'web-vitals';

import { AuthProvider }        from './context/AuthContext';
import { AdminAuthProvider }   from './context/AdminAuthContext';
import { CompanyAuthProvider } from './context/CompanyAuthContext';
import { ThemeProvider }       from './context/ThemeContext';
import { ProtectedRoute, AdminProtectedRoute } from './components/common/ProtectedRoute';
import AdminLayout   from './components/admin/AdminLayout';
import UserLayout    from './components/common/UserLayout';
import CompanyLayout, { CompanyProtectedRoute } from './components/company/CompanyLayout';
import AnnouncementBanner  from './components/common/AnnouncementBanner';
import ImpersonationBanner from './components/common/ImpersonationBanner';

// ── Eager-load user-facing pages (small bundles) ──────────────────────────────
import LoginPage        from './pages/auth/LoginPage';
import RegisterPage     from './pages/auth/RegisterPage';
import HomePage         from './pages/public/HomePage';
import BrowseRolesPage  from './pages/public/BrowseRolesPage';
import PricingPage      from './pages/public/PricingPage';
import RoleOverviewPage from './pages/dashboard/RoleOverviewPage';
import DashboardPage    from './pages/dashboard/DashboardPage';
import QuizSessionPage   from './pages/dashboard/QuizSessionPage';
import StudyChapterPage  from './pages/dashboard/StudyChapterPage';
const FlashCardPage     = lazy(() => import('./pages/dashboard/FlashCardPage'));

// ── Sentry init (uses env var — no-op if not set) ─────────────────────────────
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn:                process.env.REACT_APP_SENTRY_DSN,
    environment:        process.env.NODE_ENV,
    tracesSampleRate:   process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// Report Web Vitals to analytics
function reportWebVitals() {
  const send = ({ name, value }) => {
    if (process.env.NODE_ENV !== 'production') return;
    if (window.analytics?.track) window.analytics.track('Web Vital', { name, value: Math.round(value) });
  };
  onCLS(send); onINP(send); onLCP(send);
}

// ── Lazy-load heavy / infrequent pages ────────────────────────────────────────
const ResultsPage       = lazy(() => import('./pages/dashboard/ResultsPage'));
const SettingsPage      = lazy(() => import('./pages/dashboard/SettingsPage'));
const CertificatesPage  = lazy(() => import('./pages/dashboard/CertificatesPage'));
const JDPrepPage        = lazy(() => import('./pages/dashboard/JDPrepPage'));
const JDPrepResultsPage = lazy(() => import('./pages/dashboard/JDPrepResultsPage'));
const JDPrepQuizPage    = lazy(() => import('./pages/dashboard/JDPrepQuizPage'));
const SubscribePage     = lazy(() => import('./pages/public/SubscribePage'));

const JDPrepLandingPage  = lazy(() => import('./pages/public/JDPrepLandingPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage    = lazy(() => import('./pages/auth/VerifyEmailPage'));

const VerifyPage  = lazy(() => import('./pages/public/VerifyPage'));

// Trust / legal pages
const AboutPage   = lazy(() => import('./pages/public/AboutPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const TermsPage   = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));

// Blog
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage'));
const BlogPostPage  = lazy(() => import('./pages/blog/BlogPostPage'));

// Admin — large bundle, lazy-loaded only when user visits /admin/*
const AdminLoginPage   = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard   = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminRoles       = lazy(() => import('./pages/admin/AdminRoles'));
const AdminChapters    = lazy(() => import('./pages/admin/AdminChapters'));
const AdminSubChapters = lazy(() => import('./pages/admin/AdminSubChapters'));
const AdminQuestions   = lazy(() => import('./pages/admin/AdminQuestions'));
const AdminUsers       = lazy(() => import('./pages/admin/AdminUsers'));
const AdminBulkUpload  = lazy(() => import('./pages/admin/AdminBulkUpload'));
const AdminRefunds     = lazy(() => import('./pages/admin/AdminRefunds'));
const AdminExport      = lazy(() => import('./pages/admin/AdminExport'));
const AdminActivityLog = lazy(() => import('./pages/admin/AdminActivityLog'));
const AdminContentHealth = lazy(() => import('./pages/admin/AdminContentHealth'));
const AdminReviewQueue = lazy(() => import('./pages/admin/AdminReviewQueue'));
const AdminCoupons     = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminBroadcast   = lazy(() => import('./pages/admin/AdminBroadcast'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions').then(m => ({ default: m.AdminSubscriptions })));
const AdminPayments      = lazy(() => import('./pages/admin/AdminSubscriptions').then(m => ({ default: m.AdminPayments })));
const AdminUserAnalytics      = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminUserAnalytics })));
const AdminRevenueAnalytics   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminRevenueAnalytics })));
const AdminContentAnalytics   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminContentAnalytics })));
const AdminConversionFunnel   = lazy(() => import('./pages/admin/AdminAnalytics').then(m => ({ default: m.AdminConversionFunnel })));
const AdminCertificates = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminCertificates })));
const AdminAdmins       = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminAdmins })));
const AdminSettings     = lazy(() => import('./pages/admin/AdminMisc').then(m => ({ default: m.AdminSettings })));
const AdminJDPrep       = lazy(() => import('./pages/admin/AdminJDPrep'));
const AdminStudyContent    = lazy(() => import('./pages/admin/AdminStudyContent'));
const AdminExplainContent  = lazy(() => import('./pages/admin/AdminExplainContent'));
const AdminSupport      = lazy(() => import('./pages/admin/AdminSupport'));
const AdminBlog         = lazy(() => import('./pages/admin/AdminBlog'));
const AdminCourses         = lazy(() => import('./pages/admin/AdminCourses'));
const AdminCourseGenerate  = lazy(() => import('./pages/admin/AdminCourseGenerate'));
const AdminCourseDetail    = lazy(() => import('./pages/admin/AdminCourseDetail'));
const AdminEmailTemplates  = lazy(() => import('./pages/admin/AdminEmailTemplates'));

// Features page
const FeaturesPage    = lazy(() => import('./pages/public/FeaturesPage'));

// User-facing course pages
const CoursesPage      = lazy(() => import('./pages/public/CoursesPage'));
const CoursePage       = lazy(() => import('./pages/dashboard/CoursePage'));
const CourseTopicPage  = lazy(() => import('./pages/dashboard/CourseTopicPage'));
const CourseReaderPage = lazy(() => import('./pages/dashboard/CourseReaderPage'));
const WalletPage       = lazy(() => import('./pages/dashboard/WalletPage'));
const ReferPage        = lazy(() => import('./pages/dashboard/ReferPage'));
const AdminVouchers    = lazy(() => import('./pages/admin/AdminVouchers'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));

// Company portal
const CompanyLoginPage      = lazy(() => import('./pages/company/CompanyLoginPage'));
const CompanyRegisterPage   = lazy(() => import('./pages/company/CompanyRegisterPage'));
const CompanyActivatePage   = lazy(() => import('./pages/company/CompanyActivatePage'));
const CompanyPricingPage    = lazy(() => import('./pages/company/CompanyPricingPage'));
const CompanyDashboard      = lazy(() => import('./pages/company/CompanyDashboard'));
const AssessmentCreatePage  = lazy(() => import('./pages/company/AssessmentCreatePage'));
const AssessmentDetailPage  = lazy(() => import('./pages/company/AssessmentDetailPage'));
const CandidatesPage        = lazy(() => import('./pages/company/CandidatesPage'));
const TeamPage              = lazy(() => import('./pages/company/TeamPage'));
const BillingPage           = lazy(() => import('./pages/company/BillingPage'));
const CreditsPage           = lazy(() => import('./pages/company/CreditsPage'));
const CompanyProfilePage    = lazy(() => import('./pages/company/CompanyProfilePage'));
const AdminCompanies        = lazy(() => import('./pages/admin/AdminCompanies'));
const AdminCompanyCoupons   = lazy(() => import('./pages/admin/AdminCompanyCoupons'));
const CandidateAssessmentPage = lazy(() => import('./pages/candidate/CandidateAssessmentPage'));

// ── Fallback spinner ──────────────────────────────────────────────────────────
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f4f0)' }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

const Placeholder = ({ name }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #f5f4f0)' }}>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1B3A5C', margin: '0 0 8px' }}>{name}</h1>
      <p style={{ color: '#73726c', fontSize: 14 }}>Coming soon</p>
    </div>
  </div>
);

const Admin = ({ children }) => (
  <AdminProtectedRoute><AdminLayout>{children}</AdminLayout></AdminProtectedRoute>
);

const Shell = ({ children }) => (
  <ProtectedRoute><UserLayout>{children}</UserLayout></ProtectedRoute>
);

const Company = ({ children }) => (
  <CompanyProtectedRoute><CompanyLayout>{children}</CompanyLayout></CompanyProtectedRoute>
);

// ── Scroll to top on route change ─────────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ImpersonationHandler() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => {
    const token = searchParams.get('impersonate');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.impersonated) {
          localStorage.setItem('ip_token', token);
          setImpersonating({ name: payload.full_name || payload.email || 'User' });
          setSearchParams({}, { replace: true });
        }
      } catch {}
      return;
    }
    const stored = localStorage.getItem('ip_token');
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        if (payload.impersonated) {
          if (Date.now() < payload.exp * 1000) setImpersonating({ name: payload.full_name || payload.email || 'User' });
          else localStorage.removeItem('ip_token');
        }
      } catch {}
    }
  }, [searchParams, setSearchParams]);

  const exitImpersonation = () => { localStorage.removeItem('ip_token'); setImpersonating(null); window.location.reload(); };

  if (!impersonating) return null;
  return <ImpersonationBanner userName={impersonating.name} onExit={exitImpersonation} />;
}

export default function App() {
  useEffect(() => { reportWebVitals(); }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AdminAuthProvider>
          <CompanyAuthProvider>
            {/* Accessibility: skip navigation */}
            <a href="#main-content" className="skip-link">Skip to main content</a>

            <AnnouncementBanner />
            <ImpersonationHandler />
            <ScrollToTop />
            <Toaster position="top-right" toastOptions={{ duration: 4000, style: { fontSize: '14px', borderRadius: '10px', background: 'var(--bg-elevated, #fff)', color: 'var(--text, #1a1a18)' } }} />

            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public */}
                <Route path="/"         element={<HomePage />} />
                <Route path="/roles"    element={<BrowseRolesPage />} />
                <Route path="/pricing"  element={<PricingPage />} />
                <Route path="/interview-prep" element={<JDPrepLandingPage />} />
                <Route path="/about"    element={<AboutPage />} />
                <Route path="/contact"  element={<ContactPage />} />
                <Route path="/terms"    element={<TermsPage />} />
                <Route path="/privacy"  element={<PrivacyPage />} />
                <Route path="/blog"     element={<BlogIndexPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/verify"       element={<VerifyPage />} />
                <Route path="/verify/:code" element={<VerifyPage />} />
                <Route path="/login"    element={<LoginPage />} />
                <Route path="/register"           element={<RegisterPage />} />
                <Route path="/forgot-password"    element={<ForgotPasswordPage />} />
                <Route path="/reset-password"     element={<ResetPasswordPage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                <Route path="/roles/:slug"         element={<RoleOverviewPage />} />
                <Route path="/subscribe/:slug"     element={<SubscribePage />} />
                <Route path="/features"            element={<FeaturesPage />} />
                <Route path="/courses"             element={<CoursesPage />} />

                {/* Protected user */}
                <Route path="/dashboard"             element={<Shell><main id="main-content"><DashboardPage /></main></Shell>} />
                <Route path="/study/:subChapterId"        element={<ProtectedRoute><StudyChapterPage /></ProtectedRoute>} />
                <Route path="/flashcards/:subChapterId"  element={<ProtectedRoute><FlashCardPage /></ProtectedRoute>} />
                <Route path="/quiz/:subChapterId"    element={<ProtectedRoute><QuizSessionPage /></ProtectedRoute>} />
                <Route path="/results/:progressId"   element={<Shell><main id="main-content"><ResultsPage /></main></Shell>} />
                <Route path="/settings"              element={<Shell><main id="main-content"><SettingsPage /></main></Shell>} />
                <Route path="/certificates"          element={<Shell><main id="main-content"><CertificatesPage /></main></Shell>} />
                <Route path="/jd-prep"               element={<Shell><main id="main-content"><JDPrepPage /></main></Shell>} />
                <Route path="/jd-prep/:id"           element={<Shell><main id="main-content"><JDPrepResultsPage /></main></Shell>} />
                <Route path="/jd-prep/:id/quiz"     element={<ProtectedRoute><JDPrepQuizPage /></ProtectedRoute>} />
                <Route path="/courses/:slug"             element={<CoursePage />} />
                <Route path="/courses/topic/:topicId"    element={<CourseTopicPage />} />
                <Route path="/courses/learn/:courseId"   element={<ProtectedRoute><CourseReaderPage /></ProtectedRoute>} />
                <Route path="/wallet"                    element={<Shell><main id="main-content"><WalletPage /></main></Shell>} />
                <Route path="/refer"                     element={<Shell><main id="main-content"><ReferPage /></main></Shell>} />

                {/* Admin */}
                <Route path="/admin/login"            element={<AdminLoginPage />} />
                <Route path="/admin"                  element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard"        element={<Admin><AdminDashboard /></Admin>} />
                <Route path="/admin/roles"            element={<Admin><AdminRoles /></Admin>} />
                <Route path="/admin/chapters"         element={<Admin><AdminChapters /></Admin>} />
                <Route path="/admin/sub-chapters"     element={<Admin><AdminSubChapters /></Admin>} />
                <Route path="/admin/questions"        element={<Admin><AdminQuestions /></Admin>} />
                <Route path="/admin/users"            element={<Admin><AdminUsers /></Admin>} />
                <Route path="/admin/subscriptions"    element={<Admin><AdminSubscriptions /></Admin>} />
                <Route path="/admin/payments"         element={<Admin><AdminPayments /></Admin>} />
                <Route path="/admin/refunds"          element={<Admin><AdminRefunds /></Admin>} />
                <Route path="/admin/analytics/users"  element={<Admin><AdminUserAnalytics /></Admin>} />
                <Route path="/admin/analytics/revenue" element={<Admin><AdminRevenueAnalytics /></Admin>} />
                <Route path="/admin/analytics/content" element={<Admin><AdminContentAnalytics /></Admin>} />
                <Route path="/admin/analytics/conversion" element={<Admin><AdminConversionFunnel /></Admin>} />
                <Route path="/admin/certificates"     element={<Admin><AdminCertificates /></Admin>} />
                <Route path="/admin/admins"           element={<Admin><AdminAdmins /></Admin>} />
                <Route path="/admin/bulk-upload"      element={<Admin><AdminBulkUpload /></Admin>} />
                <Route path="/admin/settings"         element={<Admin><AdminSettings /></Admin>} />
                <Route path="/admin/activity-log"     element={<Admin><AdminActivityLog /></Admin>} />
                <Route path="/admin/content-health"   element={<Admin><AdminContentHealth /></Admin>} />
                <Route path="/admin/review-queue"     element={<Admin><AdminReviewQueue /></Admin>} />
                <Route path="/admin/coupons"          element={<Admin><AdminCoupons /></Admin>} />
                <Route path="/admin/broadcast"        element={<Admin><AdminBroadcast /></Admin>} />
                <Route path="/admin/email-templates"  element={<Admin><AdminEmailTemplates /></Admin>} />
                <Route path="/admin/export"           element={<Admin><AdminExport /></Admin>} />
                <Route path="/admin/jd-prep"          element={<Admin><AdminJDPrep /></Admin>} />
                <Route path="/admin/study-content"    element={<Admin><AdminStudyContent /></Admin>} />
                <Route path="/admin/explain-content" element={<Admin><AdminExplainContent /></Admin>} />
                <Route path="/admin/support"          element={<Admin><AdminSupport /></Admin>} />
                <Route path="/admin/blog"             element={<Admin><AdminBlog /></Admin>} />
                <Route path="/admin/companies"          element={<Admin><AdminCompanies /></Admin>} />
                <Route path="/admin/companies/:id"     element={<Admin><AdminCompanies /></Admin>} />
                <Route path="/admin/company-coupons"   element={<Admin><AdminCompanyCoupons /></Admin>} />
                <Route path="/admin/courses"           element={<Admin><AdminCourses /></Admin>} />
                <Route path="/admin/courses/generate"  element={<Admin><AdminCourseGenerate /></Admin>} />
                <Route path="/admin/courses/:id"       element={<Admin><AdminCourseDetail /></Admin>} />
                <Route path="/admin/vouchers"          element={<Admin><AdminVouchers /></Admin>} />
                <Route path="/admin/withdrawals"       element={<Admin><AdminWithdrawals /></Admin>} />

                {/* Company portal — public */}
                <Route path="/company/login"    element={<CompanyLoginPage />} />
                <Route path="/company/register" element={<CompanyRegisterPage />} />
                <Route path="/company/activate" element={<CompanyActivatePage />} />
                <Route path="/company/pricing"  element={<CompanyPricingPage />} />

                {/* Company portal — protected (sidebar layout) */}
                <Route path="/company/dashboard"                  element={<Company><CompanyDashboard /></Company>} />
                <Route path="/company/assessments"                element={<Navigate to="/company/dashboard" replace />} />
                <Route path="/company/assessments/new"            element={<Company><AssessmentCreatePage /></Company>} />
                <Route path="/company/assessments/:id"            element={<Company><AssessmentDetailPage /></Company>} />
                <Route path="/company/assessments/:id/candidates" element={<Company><CandidatesPage /></Company>} />
                <Route path="/company/team"                       element={<Company><TeamPage /></Company>} />
                <Route path="/company/credits"                    element={<Company><CreditsPage /></Company>} />
                <Route path="/company/billing"                    element={<Company><BillingPage /></Company>} />
                <Route path="/company/profile"                    element={<Company><CompanyProfilePage /></Company>} />

                {/* Candidate assessment (public) */}
                <Route path="/assess/:token"    element={<CandidateAssessmentPage />} />

                {/* 404 */}
                <Route path="*" element={
                  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg,#f5f4f0)' }}>
                    <div style={{ textAlign:'center' }}>
                      <p style={{ fontSize:64, fontWeight:800, color:'#e8e6df', margin:'0 0 8px' }}>404</p>
                      <a href="/" style={{ color:'#1B3A5C', fontSize:14 }}>← Back home</a>
                    </div>
                  </div>
                } />
              </Routes>
            </Suspense>
          </CompanyAuthProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}
